import { useContext, useRef, useState } from "react";
import { Handle, Position } from "@xyflow/react";
import { FiExternalLink } from "react-icons/fi";
import { FaGlobe } from "react-icons/fa";
import axios from "./../../utils/axiosInstance";
import { BiLoaderCircle } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { ungroupNode, updateNode } from "../../utils/flowSlice";
import { FaRegObjectUngroup } from "react-icons/fa6";
import { DarkModeContext } from "../../context/DarkModeContext";
import toast from "react-hot-toast";

const WebsiteNode = ({ data, isConnectable }) => {
  const [image, setImage] = useState(data.image || null);
  const [script, setScript] = useState(data.script || "");
  const [note, setNote] = useState(data.note || "");
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState("");

  const dispatch = useDispatch();
  const nodes = useSelector((store) => store.flow.nodes);
  const currentNode = nodes.find((node) => node.id === data.id);
  const isGrouped = !!currentNode ? !!currentNode.parentId : false;
  const unGroup = () =>
    dispatch(
      ungroupNode({
        id: data.id,
      })
    );

  const handleButtonClick = async () => {
    if (!!script || !!image) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASED_URL}/service/website`,
        {
          url,
        }
      );
      setLoading(false);
      setImage(response.data.image);
      setScript(response.data.summary);
      const tempImage = response.data.image;
      const tempScript = response.data.summary;
      dispatch(
        updateNode({
          id: data.id,
          data: { ...data, script: tempScript, image: tempImage },
        })
      );
    } catch (err) {
      console.log(err);
      if (err.status === 402) {
        toast.error(
          "You have run out of your credit. You need to upgrade your plan"
        );
      }
    }
  };
  const handleLinkButtonClick = async () => {
    if (url.startsWith("http")) {
      window.open(`${url}`, "_blank");
    } else {
      window.open(`https://${url}`, "_blank");
    }
  };

  const { darkMode } = useContext(DarkModeContext);
  const textAreaRef = useRef(null);

  const handleInputChange = (e) => {
    setNote(e.target.value);

    if (e.target.scrollHeight > 50) {
      e.target.style.height = `${e.target.scrollHeight}px`;
    }
    if (e.target.value === "") {
      e.target.style.height = `${50}px`;
    }
  };

  const handleBlur = () => {
    console.log(note);

    dispatch(
      updateNode({
        id: data.id,
        data: {
          ...data,
          note: note,
        },
      })
    );
  };

  return (
    <div>
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
        style={{
          width: "20px", // Default width
          height: "20px", // Default height
          background: "white", // Default background color
          border: "2px solid #32b5e5", // Default border color
          borderRadius: "50%", // Circular shape
          position: "absolute",
          right: "-20px", // Ensure proper alignment
          // top: "50%", // Align vertically
          transform: "translateY(-50%)", // Center the handle
          display: "flex",
          alignItems: "center",
          lineHeight: "20px", // Match the handle's height for vertical centering
          fontSize: "12px", // Ensure text size fits
          justifyContent: "center",
          transition: "transform 0.2s, background-color 0.2s, right 0.2s", // Smooth hover transition
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = "translateY(-50%) scale(1.5)"; // Keep alignment and scale
          e.target.style.background = "#32b5e5"; // Change background to blue
          e.target.style.color = "white"; // Set text color to white
          e.target.innerHTML = "+"; // Add "+" symbol inside
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = "translateY(-50%) scale(1)"; // Reset scale while maintaining alignment
          e.target.style.background = "white"; // Reset background to white
          e.target.style.color = ""; // Clear the text color
          e.target.innerHTML = ""; // Remove "+" symbol
        }}
      />

      <div
        className={`flex flex-col gap-1 max-w-md h-auto mx-auto  ${
          darkMode ? "bg-[#eeeee6]" : "bg-[#080808]"
        }`}
        tabIndex="0"
      >
        <div className="flex justify-between items-center bg-[#32b5e5] text-white px-4 py-2 rounded-[9px]">
          <div className="flex items-center space-x-2">
            {isGrouped && (
              <FaRegObjectUngroup
                size={"16"}
                className="hover:cursor-pointer"
                onClick={() => unGroup()}
              />
            )}
            <FaGlobe size={"14px"} />
            <span className="font-semibold">Website</span>
          </div>
          <div className="hover:cursor-pointer">
            <FiExternalLink size={"14px"} onClick={handleLinkButtonClick} />
          </div>
        </div>

        {image ? (
          <div className="relative">
            <img
              // src="https://placekitten.com/300/300"
              src={image}
              alt="Adorable Ginger Kitten"
              className="w-full h-auto rounded-[8px]"
              style={{ maxwidth: "100%", height: "auto", objectFit: "cover" }}
            />
          </div>
        ) : (
          <div className="bg-white p-4 rounded-[10px] shadow-inner">
            <div className="flex items-center border border-gray-300 rounded-[9px] px-4 py-1 focus-within:border-[#32b5e5]">
              <input
                type="text"
                onChange={(e) => setUrl(e.target.value)}
                value={url}
                placeholder="Enter any website url"
                className="flex-1 focus:outline-none text-gray-600 text-sm border-gray-200 "
              />
              <button
                className="bg-[#32b5e5] text-white w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#259ec4] transition-transform transform hover:scale-110"
                onClick={handleButtonClick}
                disabled={loading}
              >
                {loading ? (
                  <BiLoaderCircle className="loading-icon" color="white" />
                ) : (
                  "➜"
                )}
              </button>
            </div>
          </div>
        )}

        {/* Notes Section */}
        <textarea
          ref={textAreaRef}
          className={` nodrag w-full h-[36px] min-h-[50px] max-h-[120px] ${
            darkMode
              ? "bg-[#f5f8fa] border-neutral-300 text-black"
              : "bg-[#292929] border-[#292929] text-neutral-200"
          } text-sm font-sans  p-1.5 pr-3 rounded-[8px]  focus-within:outline-none resize-y `}
          placeholder={"Add notes for AI to use..."}
          value={note}
          onChange={(e) => handleInputChange(e)}
          onBlur={handleBlur}
          rows={1}
        ></textarea>
      </div>
    </div>
  );
};

export default WebsiteNode;
